import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import {
  buildCancelStagedActionMessage,
  buildConfirmStagedActionMessage,
} from './stagedActionMessages'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(16)};
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const PromptCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const Title = styled(Heading)`
  display: block;
`

const Body = styled(BodyText)`
  display: block;
`

const Actions = styled.div`
  display: flex;
  gap: ${rem(8)};
  flex-wrap: wrap;
`

export interface StagedActionConfirmationPromptProps {
  pendingActionId: string
  title: string
  body: string
  confirmButtonLabel: string
  cancelButtonLabel: string
  selectedAction?: 'confirm' | 'cancel'
  isHistorical?: boolean
  isStreaming?: boolean
  onSendMessage?: (message: string) => void
}

function shouldCollapseHistory({
  isHistorical,
  isExpanded,
  selectedAction,
}: {
  isHistorical: boolean
  isExpanded: boolean
  selectedAction?: 'confirm' | 'cancel'
}): boolean {
  return isHistorical && !isExpanded && !selectedAction
}

function StagedActionConfirmationPrompt({
  pendingActionId,
  title,
  body,
  confirmButtonLabel,
  cancelButtonLabel,
  selectedAction,
  isHistorical = false,
  isStreaming = false,
  onSendMessage,
}: StagedActionConfirmationPromptProps) {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState(() => !(isHistorical && !selectedAction))
  const isActionDisabled = isHistorical || isStreaming || !onSendMessage
  const handleShowOptionsClick = useCallback(() => {
    setIsExpanded(true)
  }, [])
  const handleHideOptionsClick = useCallback(() => {
    setIsExpanded(false)
  }, [])
  const handleConfirmClick = useCallback(() => {
    if (!onSendMessage) return
    onSendMessage(buildConfirmStagedActionMessage(pendingActionId))
  }, [onSendMessage, pendingActionId])
  const handleCancelClick = useCallback(() => {
    if (!onSendMessage) return
    onSendMessage(buildCancelStagedActionMessage(pendingActionId))
  }, [onSendMessage, pendingActionId])
  const labels = {
    showOptionsLabel: intl.formatMessage({
      id: 'h96BUzfKENlUYTN7sOZH',
      defaultMessage: 'Show options',
      description: 'Locked-history button label that expands a staged action confirmation prompt',
    }),
    hideOptionsLabel: intl.formatMessage({
      id: 'J2MXXfRoEIIDlbIAo0nU',
      defaultMessage: 'Hide options',
      description: 'Locked-history button label that collapses a staged action confirmation prompt',
    }),
  }

  useEffect(() => {
    setIsExpanded(!(isHistorical && !selectedAction))
  }, [isHistorical, selectedAction])

  if (selectedAction) {
    return (
      <Actions>
        <TextButton
          kind={selectedAction === 'confirm' ? 'primary' : 'secondary'}
          size="medium"
          disabled
        >
          {selectedAction === 'confirm' ? confirmButtonLabel : cancelButtonLabel}
        </TextButton>
      </Actions>
    )
  }

  if (shouldCollapseHistory({ isHistorical, isExpanded, selectedAction })) {
    return (
      <TextButton
        kind="tertiary"
        size="small"
        onClick={handleShowOptionsClick}
      >
        {labels.showOptionsLabel}
      </TextButton>
    )
  }

  return (
    <Card>
      <PromptCopy>
        <Title variant="heading6" colour="neutral-one">
          {title}
        </Title>
        <Body variant="medium" colour="neutral-one">
          {body}
        </Body>
      </PromptCopy>

      <Actions>
        <TextButton
          kind="primary"
          size="medium"
          disabled={isActionDisabled}
          onClick={handleConfirmClick}
        >
          {confirmButtonLabel}
        </TextButton>
        <TextButton
          kind="secondary"
          size="medium"
          disabled={isActionDisabled}
          onClick={handleCancelClick}
        >
          {cancelButtonLabel}
        </TextButton>
      </Actions>

      {isHistorical && (
        <div>
          <TextButton
            kind="tertiary"
            size="small"
            onClick={handleHideOptionsClick}
          >
            {labels.hideOptionsLabel}
          </TextButton>
        </div>
      )}
    </Card>
  )
}

export default StagedActionConfirmationPrompt
